import { useEffect, useRef, useState } from "react"
import { Upload, X, Image, Star, StarOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { getUploadDisplayUrl, isSameUploadFile, revokeLocalObjectUrl, type UploadablePhoto } from "@/lib/vehicleStorage"

interface PhotoUploadProps {
  photos: UploadablePhoto[]
  onPhotosUpdate: (newPhotos: UploadablePhoto[]) => void
  onMainPhotoUpdate?: (mainPhoto: UploadablePhoto | string | null) => void
  mainPhoto?: UploadablePhoto | string | null
  maxPhotos?: number
  disabled?: boolean
  uniqueId?: string
}

const PhotoUpload = ({ photos, onPhotosUpdate, onMainPhotoUpdate, mainPhoto, maxPhotos = 10, disabled = false, uniqueId }: PhotoUploadProps) => {
  const [uploading, setUploading] = useState(false)
  const inputId = uniqueId ? `photo-upload-${uniqueId}` : 'photo-upload'
  const latestPhotosRef = useRef(photos)

  useEffect(() => {
    latestPhotosRef.current = photos
  }, [photos])

  useEffect(() => {
    return () => {
      latestPhotosRef.current.forEach((photo) => revokeLocalObjectUrl(photo))
    }
  }, [])

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0]
      if (!file) return

      if (photos.length >= maxPhotos) {
        toast.error(`Máximo de ${maxPhotos} fotos atingido`)
        return
      }

      setUploading(true)

      const previewUrl = URL.createObjectURL(file)

      onPhotosUpdate([
        ...photos,
        {
          url: previewUrl,
          file,
          originalName: file.name,
        },
      ])
      toast.success("Foto adicionada com sucesso")
    } catch (error) {
      console.error("Erro ao processar foto:", error)
      toast.error("Erro ao adicionar foto")
    } finally {
      setUploading(false)
      event.target.value = ''
    }
  }

  const removePhoto = (index: number) => {
    const photoToRemove = photos[index]
    const newPhotos = photos.filter((_, i) => i !== index)
    onPhotosUpdate(newPhotos)
    revokeLocalObjectUrl(photoToRemove)
    if (onMainPhotoUpdate && isSameUploadFile(photoToRemove, mainPhoto)) {
      onMainPhotoUpdate(null)
    }
  }

  const setAsMainPhoto = (photo: UploadablePhoto) => {
    if (onMainPhotoUpdate) {
      onMainPhotoUpdate(photo)
      toast.success("Foto definida como principal")
    }
  }

  const removeMainPhoto = () => {
    if (onMainPhotoUpdate) {
      onMainPhotoUpdate(null)
      toast.success("Foto principal removida")
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="file"
          id={inputId}
          accept="image/*"
          capture="environment"
          onChange={handleFileUpload}
          disabled={uploading || disabled || photos.length >= maxPhotos}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading || disabled || photos.length >= maxPhotos}
          className="cursor-pointer"
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? "Enviando..." : "Tirar ou adicionar foto"}
        </Button>
        <span className="text-sm text-muted-foreground">
          {photos.length}/{maxPhotos} fotos
        </span>
      </div>

      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative group">
              <img
                src={getUploadDisplayUrl(photo)}
                alt={`Foto ${index + 1}`}
                className={`w-full h-20 object-cover rounded-md border ${
                  isSameUploadFile(photo, mainPhoto) ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 h-7 w-7 rounded-full shadow-sm md:h-6 md:w-6 md:opacity-0 md:transition-opacity md:group-hover:opacity-100"
                onClick={() => removePhoto(index)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
              {onMainPhotoUpdate && (
                <div className="absolute -top-2 -left-2 flex gap-1 md:opacity-0 md:transition-opacity md:group-hover:opacity-100">
                  {isSameUploadFile(photo, mainPhoto) ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 rounded-full bg-yellow-500 shadow-sm hover:bg-yellow-600 md:h-6 md:w-6"
                      onClick={removeMainPhoto}
                      disabled={disabled}
                      title="Remover como principal"
                    >
                      <StarOff className="h-3 w-3 text-white" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 rounded-full bg-gray-500 shadow-sm hover:bg-gray-600 md:h-6 md:w-6"
                      onClick={() => setAsMainPhoto(photo)}
                      disabled={disabled}
                      title="Definir como principal"
                    >
                      <Star className="h-3 w-3 text-white" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && !uploading && (
        <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-muted-foreground/25 rounded-md">
          <Image className="h-12 w-12 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground text-center">
            Nenhuma foto adicionada
          </p>
          <p className="text-xs text-muted-foreground text-center mt-1">
            No celular, o botão pode abrir direto a câmera
          </p>
        </div>
      )}
    </div>
  )
}

export default PhotoUpload
